/**
 * Focus Header — Unified bar (Option C)
 *
 * Layout: [Icon] | [Tabs...] [+ ⌘K] | [FA ▼]
 * - Logo reducido a solo ícono
 * - Tabs ocupan todo el espacio central
 * - Botón + abre el AppLauncher (⌘K)
 * - Avatar con iniciales, email en dropdown
 */



import { useState, useRef, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { useShellStore } from '@/store/shell-store'
import { usePingID } from '@/hooks/usePingID'
import { AppTabs } from './AppTabs'
import { AppLauncher } from './AppLauncher'
import {
  colors,
  typography,
  spacing,
  componentSizes,
  borderRadius,
  transitions,
  zIndex
} from '@/lib/tokens'

function UserAvatar({ email }: { email: string }) {
  const initials = email
    .split('@')[0]
    .split(/[._-]/)
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase() || '')
    .join('')

  return (
    <div style={{
      width: '26px',
      height: '26px',
      borderRadius: borderRadius.sm,
      background: colors.primary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '10px',
      fontWeight: typography.fontWeight.semibold,
      color: colors.background,
      fontFamily: typography.fontFamily.sans,
      flexShrink: 0,
    }}>
      {initials}
    </div>
  )
}

export function FocusHeader() {
  const { user, signOut } = useAuth()
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [launcherOpen, setLauncherOpen] = useState(false)
  const [logoHover, setLogoHover] = useState(false)
  const logoRef = useRef<HTMLDivElement>(null)
  const connectionStatus = useShellStore(s => s.connectionStatus)
  const connectionLatency = useShellStore(s => s.connectionLatency)
  const shellError = useShellStore(s => s.shellError)
  const pingid = usePingID()
  const userMenuRef = useRef<HTMLDivElement>(null)
  const launcherRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (err) {
      console.error('Error signing out:', err)
    }
  }

  return (
    <header style={{
      height: componentSizes.headerHeight,
      background: colors.card,
      borderBottom: `1px solid ${colors.border}`,
      display: 'flex',
      alignItems: 'center',
      paddingLeft: spacing.sm,
      paddingRight: spacing.sm,
      flexShrink: 0,
      gap: spacing.xs,
    }}>
      {/* Logo — heartbeat icon with connection status */}
      <div
        onMouseEnter={() => setLogoHover(true)}
        onMouseLeave={() => setLogoHover(false)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '32px',
          height: '32px',
          flexShrink: 0,
          position: 'relative',
          cursor: 'default',
        }}
      >
        <img
          src="/favicon.svg"
          alt="Focus"
          className={
            shellError
              ? 'focus-logo-error'
              : connectionStatus === 'connected'
                ? 'focus-logo-pulse'
                : 'focus-logo-grey'
          }
          style={{
            height: '22px',
            width: '22px',
          }}
        />
        {logoHover && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '2px',
            padding: `4px ${spacing.sm}`,
            background: colors.card,
            border: `1px solid ${colors.border}`,
            borderRadius: borderRadius.sm,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
            zIndex: zIndex.tooltip,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
            }}>
              <span style={{
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.bold,
                fontFamily: typography.fontFamily.sans,
                color: colors.textPrimary,
                letterSpacing: '0.5px',
              }}>
                FOCUS
              </span>
              <span style={{
                fontSize: '9px',
                fontFamily: typography.fontFamily.mono,
                color: colors.primary,
                background: `${colors.primary}15`,
                padding: '1px 5px',
                borderRadius: borderRadius.sm,
                fontWeight: typography.fontWeight.medium,
              }}>
                beta
              </span>
              <span style={{
                fontSize: '9px',
                fontFamily: typography.fontFamily.mono,
                color: shellError
                  ? colors.error
                  : connectionStatus === 'connected'
                    ? colors.success
                    : colors.textMuted,
                fontWeight: typography.fontWeight.medium,
              }}>
                {shellError
                  ? shellError
                  : connectionStatus === 'connected'
                    ? `${connectionLatency ?? '—'}ms`
                    : connectionStatus === 'degraded'
                      ? 'Reconectando...'
                      : 'Sin conexión'}
              </span>
            </div>
            <span style={{
              fontSize: '9px',
              fontFamily: typography.fontFamily.mono,
              color: colors.textMuted,
              fontWeight: typography.fontWeight.medium,
            }}>
              PingID {pingid.buys ?? '···'}.{pingid.sells ?? '···'}{pingid.stale ? '?' : ''}
            </span>
          </div>
        )}
      </div>

      {/* Separator */}
      <div style={{
        width: '1px',
        height: '20px',
        background: colors.border,
        flexShrink: 0,
      }} />

      {/* AppTabs — fill remaining space */}
      <div style={{ flex: 1, overflow: 'hidden', height: '100%' }}>
        <AppTabs />
      </div>

      {/* Add app button — opens launcher */}
      <div ref={launcherRef} style={{ position: 'relative', flexShrink: 0 }}>
        <AppLauncher
          isOpen={launcherOpen}
          onToggle={() => setLauncherOpen(!launcherOpen)}
          onClose={() => setLauncherOpen(false)}
        />
      </div>

      {/* User menu */}
      <div ref={userMenuRef} style={{ position: 'relative', flexShrink: 0 }}>
        <button
          onClick={() => setUserMenuOpen(!userMenuOpen)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: spacing.xs,
            padding: `3px`,
            background: 'transparent',
            border: `1px solid transparent`,
            borderRadius: borderRadius.full,
            cursor: 'pointer',
            transition: transitions.fast,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = colors.border
          }}
          onMouseLeave={(e) => {
            if (!userMenuOpen) e.currentTarget.style.borderColor = 'transparent'
          }}
          title={user?.email || ''}
        >
          <UserAvatar email={user?.email || ''} />
          <span style={{
            fontSize: '8px',
            color: colors.textMuted,
            marginRight: '2px',
          }}>
            ▼
          </span>
        </button>

        {userMenuOpen && (
          <div style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            background: colors.card,
            border: `1px solid ${colors.border}`,
            borderRadius: borderRadius.md,
            padding: spacing.sm,
            minWidth: '200px',
            zIndex: zIndex.dropdown,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
          }}>
            <div style={{
              padding: `${spacing.sm} ${spacing.sm}`,
              borderBottom: `1px solid ${colors.border}`,
              marginBottom: spacing.sm,
            }}>
              <div style={{
                fontSize: typography.fontSize.sm,
                color: colors.textPrimary,
                fontWeight: typography.fontWeight.medium,
                fontFamily: typography.fontFamily.sans,
              }}>
                {user?.email}
              </div>
            </div>
            <button
              onClick={handleSignOut}
              style={{
                width: '100%',
                padding: spacing.sm,
                background: 'transparent',
                border: 'none',
                borderRadius: borderRadius.sm,
                color: colors.error,
                cursor: 'pointer',
                transition: transitions.fast,
                textAlign: 'left',
                fontSize: typography.fontSize.sm,
                fontFamily: typography.fontFamily.sans,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = colors.cardHover
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent'
              }}
            >
              Salir
            </button>
          </div>
        )}
      </div>
    </header>
  )
}
